// mcn-frontend/src/components/common/HelpAssistant.jsx
//
// Floating AI Help Assistant widget. Renders a bubble in the bottom-right
// that expands into a chat panel. Backed by /api/help/ask (Gemini via
// UnifiedAIService) which is fed a Choice MCN knowledge base.
//
// Usage:
//   <HelpAssistant />        // floating button + panel
//   <HelpAssistant embedded /> // inline (no floating button)
//
// No PII collected. Includes a hidden honeypot field in submissions.
import React, { useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../../config';

const GRADIENT = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

const INITIAL_GREETING = {
  role: 'assistant',
  content:
    "Hi! I'm the Choice MCN assistant. Ask me about applying, payouts, KYC, " +
    "Common Channels, security, or anything else about the platform.",
};

const SUGGESTED = [
  'How do I apply as a creator?',
  'When do I get paid?',
  'How do I avoid copyright strikes on YouTube?',
  'What does the YouTube algorithm reward?',
];

const ChatPanel = ({ onClose, embedded }) => {
  const [messages, setMessages] = useState([INITIAL_GREETING]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  useEffect(() => {
    // Focus the input when the panel opens
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const send = async (questionOverride) => {
    const question = (questionOverride ?? input).trim();
    if (!question || loading) return;
    setInput('');
    setError('');

    // Append user message immediately
    const next = [...messages, { role: 'user', content: question }];
    setMessages(next);
    setLoading(true);

    try {
      const history = next
        .slice(-6)
        .map((m) => ({ role: m.role, content: m.content }));

      const res = await fetch(`${API_BASE_URL}/api/help/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question,
          history,
          website: '', // honeypot — must stay empty
        }),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || !json.success) {
        throw new Error(json.error || `HTTP ${res.status}`);
      }

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            json.answer ||
            "I couldn't form an answer. Try rephrasing, or email support@choicemcn.com.",
        },
      ]);
    } catch (err) {
      setError(
        err.message?.toLowerCase().includes('too many')
          ? 'Too many questions — please wait a moment.'
          : 'The assistant is briefly unavailable. Please try again, or email support@choicemcn.com.'
      );
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const panelStyle = embedded
    ? {
        background: '#ffffff',
        borderRadius: 12,
        boxShadow: '0 4px 14px rgba(102,126,234,0.10)',
        border: '1px solid #e5e7eb',
        display: 'flex',
        flexDirection: 'column',
        height: 520,
        overflow: 'hidden',
      }
    : {
        position: 'fixed',
        bottom: 96,
        right: 24,
        width: 'min(380px, calc(100vw - 32px))',
        height: 'min(560px, calc(100vh - 140px))',
        background: '#ffffff',
        borderRadius: 16,
        boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 9999,
      };

  return (
    <div style={panelStyle}>
      {/* Header */}
      <div
        style={{
          background: GRADIENT,
          color: '#fff',
          padding: '14px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
            }}
            aria-hidden
          >
            ✦
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.1 }}>
              Choice MCN Assistant
            </div>
            <div style={{ fontSize: 11, opacity: 0.85 }}>
              Choice MCN + YouTube creator help
            </div>
          </div>
        </div>
        {!embedded && (
          <button
            onClick={onClose}
            aria-label="Close help assistant"
            style={{
              background: 'transparent',
              border: 'none',
              color: '#fff',
              fontSize: 22,
              cursor: 'pointer',
              padding: 4,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        )}
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 16,
          background: '#f8fafc',
        }}
      >
        {messages.map((m, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: 10,
            }}
          >
            <div
              style={{
                maxWidth: '85%',
                background: m.role === 'user' ? GRADIENT : '#ffffff',
                color: m.role === 'user' ? '#fff' : '#1f2937',
                padding: '10px 13px',
                borderRadius: 12,
                fontSize: 14,
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                boxShadow:
                  m.role === 'user'
                    ? '0 2px 6px rgba(102,126,234,0.25)'
                    : '0 1px 3px rgba(0,0,0,0.06)',
                border:
                  m.role === 'assistant' ? '1px solid #e5e7eb' : 'none',
              }}
            >
              {m.content}
            </div>
          </div>
        ))}

        {loading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div
              style={{
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 12,
                padding: '10px 13px',
                fontSize: 14,
                color: '#6b7280',
              }}
            >
              <span className="dots">Thinking</span>
              <style>{`
                .dots::after {
                  content: '';
                  animation: helpdots 1.2s steps(4, end) infinite;
                }
                @keyframes helpdots {
                  0%   { content: ''; }
                  25%  { content: '.'; }
                  50%  { content: '..'; }
                  75%  { content: '...'; }
                  100% { content: ''; }
                }
              `}</style>
            </div>
          </div>
        )}

        {error && (
          <div
            style={{
              background: '#fef2f2',
              color: '#991b1b',
              border: '1px solid #fecaca',
              borderRadius: 10,
              padding: '8px 12px',
              fontSize: 13,
              marginTop: 4,
            }}
          >
            {error}
          </div>
        )}

        {messages.length === 1 && (
          <div style={{ marginTop: 12 }}>
            <div
              style={{
                fontSize: 11,
                color: '#6b7280',
                textTransform: 'uppercase',
                fontWeight: 600,
                letterSpacing: 0.6,
                marginBottom: 6,
              }}
            >
              Try asking
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {SUGGESTED.map((q) => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  style={{
                    textAlign: 'left',
                    background: '#ffffff',
                    border: '1px solid #e5e7eb',
                    color: '#374151',
                    padding: '8px 12px',
                    borderRadius: 10,
                    fontSize: 13,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#a78bfa';
                    e.currentTarget.style.background = '#faf5ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.background = '#ffffff';
                  }}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <div
        style={{
          borderTop: '1px solid #e5e7eb',
          padding: 10,
          background: '#fff',
        }}
      >
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask about Choice MCN or YouTube creator tips…"
            rows={1}
            disabled={loading}
            style={{
              flex: 1,
              resize: 'none',
              border: '1px solid #e5e7eb',
              borderRadius: 10,
              padding: '10px 12px',
              fontSize: 14,
              outline: 'none',
              fontFamily: 'inherit',
              maxHeight: 120,
            }}
          />
          <button
            onClick={() => send()}
            disabled={loading || !input.trim()}
            style={{
              background: GRADIENT,
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              padding: '10px 14px',
              fontSize: 14,
              fontWeight: 600,
              cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
              opacity: loading || !input.trim() ? 0.55 : 1,
              boxShadow: '0 2px 6px rgba(102,126,234,0.25)',
            }}
          >
            Send
          </button>
        </div>
        <div
          style={{
            fontSize: 10,
            color: '#9ca3af',
            marginTop: 6,
            textAlign: 'center',
          }}
        >
          AI assistant — for personalised account help, open a ticket at /help.
        </div>
      </div>
    </div>
  );
};

const HelpAssistant = ({ embedded = false }) => {
  const [open, setOpen] = useState(embedded);

  if (embedded) {
    return <ChatPanel embedded />;
  }

  return (
    <>
      {open && <ChatPanel onClose={() => setOpen(false)} />}

      {/* Floating button */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close help assistant' : 'Open help assistant'}
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 60,
          height: 60,
          borderRadius: '50%',
          background: GRADIENT,
          color: '#fff',
          border: 'none',
          cursor: 'pointer',
          boxShadow: '0 8px 24px rgba(102,126,234,0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 26,
          zIndex: 9998,
          transition: 'transform 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.06)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        {open ? '×' : '💬'}
      </button>
    </>
  );
};

export default HelpAssistant;
